import React from 'react';
import {Map, List} from 'immutable';
import {
  withMeta,
  withSystem,
  ElementPreset,
  Plugins,
  Atom,
} from '@packages/systems/wfdl';
import {excludeSearchByDefaultType} from '@packages/systems/search';
import {typeName, qualify, iconName} from '@packages/systems/wfdl-Qualified';
import {
  getInPojo,
  getBooleanOrElse,
  getTagOrElse,
  getChildren,
} from '@packages/utilities/data-utils';
import {
  ADD_TO_CART_STATES as STATES,
  QUANTITY_ENABLED,
  STATE,
  DATA_ATTR_COMMERCE_PRODUCT_ID,
  PRODUCTS_BINDING_CONTEXT_EXTERNAL_KEY,
} from '@packages/systems/commerce/constants';
import {getProductDefaultSkuField} from '@packages/systems/commerce/utils/CommerceCollectionUtils';
import type {PluginName} from '@packages/systems/wfdl-Qualified';
import {buildAddToCartForDefaultSku} from './CommerceUtils';
import {SHARED_FORM_WRAPPER_CONSTRAINTS_DESCENDANTS} from '@packages/systems/core/constants/SharedConstraintRules';

import {
  TListOfElement,
  TRecord,
  TText,
  TEnum,
  TTypeReference,
  TTypeConstructor,
} from '@packages/systems/wfdl-ast';
import {createIntegratedAtom} from '@packages/utilities/integrated-types';
import {isUserComponentRoot} from '@packages/utilities/platform';

const CommerceAddToCartWrapper = Atom.createRender((props) => {
  const {
    renderChild,
    data,
    productId,
    attributes,
    classes,
    generateAttributes,
  } = props;
  const state = getInPojo(data, ['temp', 'state']) ?? STATES.DEFAULT;

  const context = Map({
    [STATE]: state,
    [QUANTITY_ENABLED]: getBooleanOrElse(
      getInPojo(data, ['commerce', QUANTITY_ENABLED]),
      true
    ),
    [DATA_ATTR_COMMERCE_PRODUCT_ID]: productId,
    showAddToCartButton: getBooleanOrElse(
      getInPojo(data, ['showAddToCartButton']),
      true
    ),
    showBuyNowButton: getBooleanOrElse(
      getInPojo(data, ['showBuyNowButton']),
      true
    ),
  });

  const Tag = getTagOrElse(getInPojo(data, ['tag']), 'div');

  const children = getChildren(data).map((child) =>
    renderChild(child, context)
  );

  return <Tag {...generateAttributes(attributes, classes)}>{children}</Tag>;
});

export const createCommerceAddToCartWrapper: ElementPreset.ExpressionEscapeHatchFactory =
  ({collectionState}) => {
    if (collectionState === undefined) {
      throw Error(
        'collectionState is required to create CommerceAddToCartWrapper'
      );
    }
    const productDefaultSkuField = getProductDefaultSkuField(
      collectionState.collections
    );
    const defaultSkuFieldId =
      productDefaultSkuField && productDefaultSkuField.id;

    return buildAddToCartForDefaultSku(String(defaultSkuFieldId));
  };

const staticBindings = Map({
  productId: List([
    Map({
      id: 'id',
      slug: 'id',
      type: 'PlainText',
    }),
  ]),
});

const config = createIntegratedAtom({
  displayName: 'Add to Cart',
  icon: {
    type: 'Component',
    value: qualify(Plugins.name('Commerce'), iconName('AddToCartElement')),
  },
  showEmpty: true,
  constraints: {
    descendants: [
      ...SHARED_FORM_WRAPPER_CONSTRAINTS_DESCENDANTS,
      {is: 'CommerceAddToCartForm', rule: 'ExactlyOne'},
      {is: 'CommerceAddToCartError', rule: 'ExactlyOne'},
      {is: 'CommerceAddToCartOutOfStock', rule: 'ExactlyOne'},
    ],
    ancestors: [
      {
        is: {
          check: (element, componentMap) =>
            isUserComponentRoot(componentMap, element),
          name: 'Symbol',
        },
        rule: 'Forbid',
      },
      {
        is: {
          check: (
            _element,
            _componentMap,
            bindingContextMatchesExternalKey:
              | ((arg1: PluginName, arg2: string) => boolean)
              | null
          ) =>
            Boolean(
              bindingContextMatchesExternalKey &&
              bindingContextMatchesExternalKey(
                Plugins.name('Commerce'),
                PRODUCTS_BINDING_CONTEXT_EXTERNAL_KEY
              )
            ),
          name: 'Product Page or Product Collection List',
        },
        rule: 'BindingContext',
      },
    ],
  },
  staticBindings,
  render: CommerceAddToCartWrapper,
  fields: {
    temp: TRecord({
      state: withSystem(
        {
          label: 'State',
          defaultValue: STATES.DEFAULT,
        },
        TEnum({
          [STATES.DEFAULT]: 'Default',
          [STATES.OUT_OF_STOCK]: 'Out of stock',
          [STATES.ERROR]: 'Error',
        })
      ),
    }),
    tag: withSystem({private: true}, TText),
    // To play nice with legacy nodes we're keeping this data under commerce for now
    commerce: TRecord(
      {
        type: withSystem({private: true}, TText),
        quantityEnabled: withMeta(
          {
            labels: {
              true: 'Show',
              false: 'Hide',
            },
          },
          withSystem(
            {label: 'Quantity', defaultValue: true},
            TTypeConstructor(
              qualify(Plugins.name('Basic'), typeName('LabeledBoolean'))
            )
          )
        ),
      },
      TTypeReference('extraCommerceFields')
    ),
    showAddToCartButton: withMeta(
      {
        labels: {
          true: 'Show',
          false: 'Hide',
        },
      },
      withSystem(
        {label: 'Add to cart', defaultValue: true},
        TTypeConstructor(
          qualify(Plugins.name('Basic'), typeName('LabeledBoolean'))
        )
      )
    ),
    showBuyNowButton: withMeta(
      {
        labels: {
          true: 'Show',
          false: 'Hide',
        },
      },
      withSystem(
        {label: 'Buy now', defaultValue: true},
        TTypeConstructor(
          qualify(Plugins.name('Basic'), typeName('LabeledBoolean'))
        )
      )
    ),
    note: withMeta(
      {
        content:
          'Buy now is only supported with Stripe. This button will be hidden if Stripe is not connected.',
        universityLink: 'https://university.webflow.com/lesson/add-to-cart',
      },
      withSystem(
        {defaultValue: false},
        TTypeConstructor(
          qualify(Plugins.name('Commerce'), typeName('DismissibleNote'))
        )
      )
    ),
    search: withSystem(
      {private: true},
      TRecord({
        exclude: excludeSearchByDefaultType,
      })
    ),
    children: TListOfElement,
  },
});

export default config;
